import { Button } from "@/components/ui/button";
import { useGetAuthorizedOTP } from "@/hooks/use-mutations";
import { getInitials } from "@/lib/utils";
import { useModalStore } from "@/zustand/modalStore";

const iconMap: Record<string, string> = {
  USDT: "/icons/usdt.svg",
  USDC: "/icons/usdc.svg",
};

const networkIconMap: Record<string, string> = {
  TRC20: "/icons/trc20.png",
  ERC20: "/icons/erc20.svg",
};

const ConfirmPayment = ({
  payee,
  amount,
  asset,
  onDone,
}: {
  payee: Beneficiary;
  amount: string;
  asset: any;
  onDone: () => void;
}) => {
  const { openModal } = useModalStore();
  const { mutateAsync: requestOtp, isPending } = useGetAuthorizedOTP();

  const payeeName = `${payee.firstName} ${payee.lastName}`.trim();
  const symbol = asset?.token.symbol ?? "";
  const icon = iconMap[asset?.token.symbol] ?? "/icons/usdt.svg";
  const networkIcon = networkIconMap[asset?.token.type];

  const onSubmit = async () => {
    try {
      await requestOtp({
        purpose: "internal-transfer",
        /* The recipient's address, not the sender's — but the code is
           delivered to the sender's inbox. Mirrors the mobile client; flagged
           with backend as probably-unintended. */
        emailAddress: payee.email,
        amount: Number(amount),
        toUserId: payee.id,
        assetId: asset.tokenId,
      });
      openModal("transferOtp", { payee, amount, asset, onDone });
    } catch {
      // useGetAuthorizedOTP already surfaces the error toast.
    }
  };

  return (
    <div className="relative z-[999]">
      {/* Pulled onto the close button's row — the shell renders that button
          inline on mobile, so a static heading would sit below it. */}
      <h3 className="absolute -top-12 left-0 flex h-6 w-full items-center justify-center font-semibold lg:static lg:mt-6">
        Confirm Payment
      </h3>

      <div className="mt-6 flex items-center gap-4 lg:mt-4">
        <div className="bg-dark-primary-gradient text-dark-text-400 flex size-12 shrink-0 items-center justify-center rounded-full font-medium">
          {getInitials(payeeName)}
        </div>
        <div className="min-w-0">
          <p className="text-xl">{payeeName}</p>
          <p className="text-dark-text-300 truncate text-sm">{payee.email}</p>
        </div>
      </div>

      <div className="border-dark-stroke-3 mt-6 space-y-4 rounded-2xl border p-4 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="text-dark-text-300">Send fee (0%)</span>
          <span>0 {symbol}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-dark-text-300">Payee will receive</span>
          <span className="flex items-center gap-2">
            <span className="relative size-5 shrink-0">
              <img src={icon} alt={symbol} className="size-full" />
              {networkIcon && (
                <img
                  src={networkIcon}
                  alt={asset?.token.type}
                  className="absolute -right-1 -bottom-1 size-2.5 rounded-full"
                />
              )}
            </span>
            {amount} {symbol}
          </span>
        </div>
      </div>

      <div className="border-dark-stroke-3 mt-4 rounded-2xl border p-4 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="text-dark-text-300">Method</span>
          <span>Internal Transfer</span>
        </div>
      </div>

      <Button
        isLoading={isPending}
        disabled={isPending}
        onClick={onSubmit}
        className="text-dark-text-400 bg-dark-primary-gradient mt-6 h-12 w-full rounded-lg disabled:opacity-50"
      >
        Continue
      </Button>
    </div>
  );
};

export default ConfirmPayment;
